import { c as _c } from "react/compiler-runtime";
import { EntityUtils } from '@record-it-npm/synchro-client';
import React, { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useEventCallback } from 'record-it-components/hooks/useEventCallback';
import { PageContentContainer } from 'record-it-components/pageContentLayout/PageContentContainer/PageContentContainer';
import { PageContentHeader } from 'record-it-components/pageContentLayout/PageContentHeader/PageContentHeader';
import { prepareProps } from 'record-it-components/utils/propUtils';
import { PropsWithoutChildren } from 'record-it-components/utils/utilityTypes';
import { EntityName } from 'record-it-time-common/EntityName';
import { iconConfigurations } from '../../../classes/icon/iconConfigurations';
import { RoutePaths } from '../../../classes/RoutePaths/RoutePaths';
import { useRequiredWorkTimeEntryToCreate } from '../../../hooks/createWorkTimeEntry/useRequiredWorkTimeEntryToCreate';
import { useEntityRepository } from '../../../hooks/entity/useEntityRepository';
import { useAppDispatch } from '../../../store/hooks';
import { createWorkTimeEntrySlice } from '../../../store/slice/createWorkTimeEntry/createWorkTimeEntrySlice';
import { TimeTrackingActivityToSelectListItem, TimeTrackingActivityToSelectListItemProps } from '../../timeTracking/TimeTrackingActivityToSelectListItem/TimeTrackingActivityToSelectListItem';
import { useConfigureCreateWorkTimeEntrySelectActivityPageBreadcrumbs } from './useConfigureCreateWorkTimeEntrySelectActivityPageBreadcrumbs';
import { useCreateWorkTimeEntrySelectActivityPageActivities } from './useCreateWorkTimeEntrySelectActivityPageActivities';
export type CreateWorkTimeEntrySelectActivityPageProps = PropsWithoutChildren<Record<never, never>>;
export const CreateWorkTimeEntrySelectActivityPage: FunctionComponent<CreateWorkTimeEntrySelectActivityPageProps> = React.memo(props => {
  const $ = _c(16);
  prepareProps({
    props,
    ignoreChildren: true
  });
  const {
    t
  } = useTranslation();
  const workTimeEntryToCreate = useRequiredWorkTimeEntryToCreate();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const {
    parentActivityId
  } = useParams();
  const activities = useCreateWorkTimeEntrySelectActivityPageActivities(workTimeEntryToCreate, parentActivityId ?? null);
  const timeTrackingActivityRepository = useEntityRepository(EntityName.TimeTrackingActivity);
  useConfigureCreateWorkTimeEntrySelectActivityPageBreadcrumbs(parentActivityId);
  let t0;
  if ($[0] !== dispatch || $[1] !== navigate || $[2] !== timeTrackingActivityRepository) {
    t0 = activity => {
      const childActivities = timeTrackingActivityRepository.getByParentActivityId(activity.id);
      if (!childActivities.length) {
        dispatch(createWorkTimeEntrySlice.actions.setTimeTrackingActivityId(activity.id));
        navigate(RoutePaths.createWorkTimeEntry());
      } else {
        navigate(RoutePaths.createWorkTimeEntrySelectActivity(activity.id));
      }
    };
    $[0] = dispatch;
    $[1] = navigate;
    $[2] = timeTrackingActivityRepository;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const handleActivitySelected = useEventCallback(t0);
  let t1;
  if ($[4] !== t) {
    t1 = t("components.page.CreateWorkTimeEntrySelectActivityPage.title");
    $[4] = t;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== t1) {
    t2 = <PageContentHeader title={t1} iconConfig={iconConfigurations.prefixed.page.CreateWorkTimeEntrySelectActivityPage.titleIcon} />;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== activities || $[9] !== handleActivitySelected) {
    let t4;
    if ($[11] !== handleActivitySelected) {
      t4 = a => <TimeTrackingActivityToSelectListItem key={EntityUtils.getTrackingKey(a)} timeTrackingActivity={a} onActivitySelected={handleActivitySelected} data-testid="CreateWorkTimeEntrySelectActivityPage.ActivityListItem" />;
      $[11] = handleActivitySelected;
      $[12] = t4;
    } else {
      t4 = $[12];
    }
    t3 = activities.map(t4);
    $[8] = activities;
    $[9] = handleActivitySelected;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[13] !== t2 || $[14] !== t3) {
    t4 = <PageContentContainer>{t2}{t3}</PageContentContainer>;
    $[13] = t2;
    $[14] = t3;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  return t4;
});